// src/components/admin/AdminOrders.js
import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../lib/AppContext';
import { formatEurCheckout } from '../../lib/currency';

const STATUSES = ['pending','paid','processing','shipped','delivered','cancelled','refunded'];
const S_COLORS = { pending:'#f0a500', paid:'#4cca74', processing:'#4a9edd', shipped:'#4a9edd', delivered:'#4cca74', cancelled:'#e05050', refunded:'#888' };

export default function AdminOrders() {
  const { showToast } = useApp();
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('all');
  const [search, setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null);
  // Inline edit state per order
  const [trackingEdit, setTrackingEdit] = useState({});
  const [notesEdit, setNotesEdit]       = useState({});

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) { showToast('Failed to load orders', 'error'); return; }
    setOrders(data || []);
    setLoading(false);
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (orderId, status) => {
    const { error } = await supabase
      .from('orders')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', orderId);
    if (error) { showToast('Update failed', 'error'); return; }
    await supabase.from('order_status_log').insert({ order_id: orderId, status, note: 'Updated by admin' });
    setOrders(o => o.map(x => x.id === orderId ? { ...x, status } : x));
    showToast(`Status → ${status}`, 'success');
  };

  const saveTracking = async (orderId) => {
    const tracking = trackingEdit[orderId] ?? '';
    const { error } = await supabase
      .from('orders')
      .update({ tracking_number: tracking, updated_at: new Date().toISOString() })
      .eq('id', orderId);
    if (error) { showToast('Save failed', 'error'); return; }
    setOrders(o => o.map(x => x.id === orderId ? { ...x, tracking_number: tracking } : x));
    setTrackingEdit(t => { const n={...t}; delete n[orderId]; return n; });
    showToast('Tracking saved', 'success');
  };

  const saveNotes = async (orderId) => {
    const notes = notesEdit[orderId] ?? '';
    const { error } = await supabase
      .from('orders')
      .update({ notes, updated_at: new Date().toISOString() })
      .eq('id', orderId);
    if (error) { showToast('Save failed', 'error'); return; }
    setOrders(o => o.map(x => x.id === orderId ? { ...x, notes } : x));
    setNotesEdit(n => { const c={...n}; delete c[orderId]; return c; });
    showToast('Notes saved', 'success');
  };

  // Filter + search
  const visible = orders.filter(o => {
    if (filter !== 'all' && o.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        o.id.toLowerCase().includes(q) ||
        o.customer_name?.toLowerCase().includes(q) ||
        o.customer_email?.toLowerCase().includes(q) ||
        (o.discount_code || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:300 }}>
      <div className="spinner" />
    </div>
  );

  return (
    <div className="page-container" style={{ padding:'32px 24px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <h2 style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1.8rem', letterSpacing:'.05em' }}>
          Orders <span style={{ fontSize:'1rem', color:'#555', fontFamily:"'Barlow Condensed',sans-serif", letterSpacing:'.06em' }}>({orders.length} total)</span>
        </h2>
      </div>

      {/* Search bar */}
      <div style={{ marginBottom:16 }}>
        <input
          className="input-field"
          placeholder="Search by name, email, order ID or discount code…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 480 }}
        />
      </div>

      {/* Status filter tabs */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:20 }}>
        {['all', ...STATUSES].map(s => {
          const count = s === 'all' ? orders.length : orders.filter(o=>o.status===s).length;
          return (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding:'5px 14px', border:'none', borderRadius:20,
              background: filter===s?'rgba(76,202,116,.15)':'#111',
              color: filter===s?'#4cca74':'#555',
              fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.75rem',
              fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer',
            }}>
              {s} {count > 0 && <span style={{ opacity:.6 }}>({count})</span>}
            </button>
          );
        })}
      </div>

      <div style={{ background:'#111', borderRadius:16, border:'1px solid rgba(255,255,255,.07)', overflow:'hidden' }}>
        {visible.length === 0 && (
          <div style={{ textAlign:'center', padding:'3rem', color:'#555' }}>
            {search ? `No orders matching "${search}"` : 'No orders found'}
          </div>
        )}

        {visible.map(order => (
          <div key={order.id} style={{ borderBottom:'1px solid rgba(255,255,255,.04)' }}>
            {/* Row */}
            <button
              onClick={() => setExpanded(expanded === order.id ? null : order.id)}
              style={{ width:'100%', background:'none', border:'none', padding:'16px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', gap:12 }}
              onMouseOver={e => e.currentTarget.style.background='rgba(255,255,255,.02)'}
              onMouseOut={e  => e.currentTarget.style.background='none'}
            >
              <div style={{ textAlign:'left', flex:1, minWidth:0 }}>
                <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.9rem', fontWeight:700, color:'#ccc', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  #{order.id.slice(0,8).toUpperCase()} · {order.customer_name}
                </div>
                <div style={{ fontSize:'.75rem', color:'#555', marginTop:2 }}>
                  {order.customer_email} · {new Date(order.created_at).toLocaleDateString()}
                  {order.discount_code && <span style={{ color:'#9b59b6', marginLeft:8 }}>🎟 {order.discount_code}</span>}
                  {order.tracking_number && <span style={{ color:'#4a9edd', marginLeft:8 }}>📦 {order.tracking_number}</span>}
                </div>
              </div>
              <div style={{ textAlign:'right', display:'flex', alignItems:'center', gap:16, flexShrink:0 }}>
                <div>
                  <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1.15rem', color:'#4cca74' }}>{formatEurCheckout(order.total_eur)}</div>
                  <div style={{ fontSize:'.7rem', color:S_COLORS[order.status]||'#888', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase' }}>{order.status}</div>
                </div>
                <span style={{ color:'#444', fontSize:'1rem', transform: expanded===order.id?'rotate(90deg)':'none', transition:'transform .2s' }}>›</span>
              </div>
            </button>

            {/* Expanded detail */}
            {expanded === order.id && (
              <div style={{ padding:'0 20px 20px', background:'rgba(255,255,255,.015)' }}>
                {/* Items */}
                <div style={{ marginBottom:14, paddingTop:8 }}>
                  <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.68rem', color:'#555', letterSpacing:'.1em', textTransform:'uppercase', marginBottom:6 }}>Items</div>
                  {(order.items||[]).map((item, i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:'.85rem', marginBottom:4 }}>
                      <span style={{ color:'#888' }}>{item.name} × {item.qty}</span>
                      <span style={{ color:'#f4f4f4' }}>{formatEurCheckout(item.price_eur * item.qty)}</span>
                    </div>
                  ))}
                  <div style={{ height:1, background:'rgba(255,255,255,.05)', margin:'8px 0' }} />
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.82rem', marginBottom:3 }}>
                    <span style={{ color:'#555' }}>Subtotal</span>
                    <span>{formatEurCheckout(order.subtotal_eur)}</span>
                  </div>
                  {order.discount_eur > 0 && (
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.82rem', marginBottom:3 }}>
                      <span style={{ color:'#9b59b6' }}>Discount ({order.discount_code})</span>
                      <span style={{ color:'#9b59b6' }}>−{formatEurCheckout(order.discount_eur)}</span>
                    </div>
                  )}
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.82rem', marginBottom:3 }}>
                    <span style={{ color:'#555' }}>Shipping</span>
                    <span>{order.shipping_eur === 0 ? 'Free' : formatEurCheckout(order.shipping_eur)}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'.9rem', fontWeight:700, color:'#4cca74' }}>
                    <span>Total</span>
                    <span>{formatEurCheckout(order.total_eur)}</span>
                  </div>
                </div>

                {/* Shipping address */}
                {order.shipping_address && (
                  <div style={{ fontSize:'.8rem', color:'#555', marginBottom:14, lineHeight:1.8 }}>
                    <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.68rem', color:'#555', letterSpacing:'.1em', textTransform:'uppercase', marginBottom:4 }}>Ship to</div>
                    {order.shipping_address.full_name}<br />
                    {order.shipping_address.address_line1}<br />
                    {order.shipping_address.city}, {order.shipping_address.postal_code} · {order.shipping_address.country_code}
                    {order.shipping_address.phone && <><br />{order.shipping_address.phone}</>}
                  </div>
                )}

                {/* Tracking number */}
                <div style={{ marginBottom:14 }}>
                  <div className="input-label">Tracking Number</div>
                  <div style={{ display:'flex', gap:8 }}>
                    <input
                      className="input-field"
                      value={trackingEdit[order.id] ?? order.tracking_number ?? ''}
                      onChange={e => setTrackingEdit(t => ({ ...t, [order.id]: e.target.value }))}
                      placeholder="e.g. 1Z999AA10123456784"
                      style={{ flex:1 }}
                    />
                    {(trackingEdit[order.id] !== undefined) && (
                      <button onClick={() => saveTracking(order.id)} style={{ background:'#4cca74', border:'none', borderRadius:10, padding:'0 16px', color:'#000', fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.78rem', fontWeight:800, cursor:'pointer', whiteSpace:'nowrap' }}>
                        Save
                      </button>
                    )}
                  </div>
                </div>

                {/* Internal notes */}
                <div style={{ marginBottom:16 }}>
                  <div className="input-label">Internal Notes (not visible to customer)</div>
                  <div style={{ display:'flex', gap:8, flexDirection:'column' }}>
                    <textarea
                      className="input-field"
                      rows={2}
                      value={notesEdit[order.id] ?? order.notes ?? ''}
                      onChange={e => setNotesEdit(n => ({ ...n, [order.id]: e.target.value }))}
                      placeholder="Add internal notes…"
                      style={{ resize:'vertical' }}
                    />
                    {(notesEdit[order.id] !== undefined) && (
                      <button onClick={() => saveNotes(order.id)} style={{ background:'rgba(76,202,116,.1)', border:'1px solid rgba(76,202,116,.25)', borderRadius:10, padding:'8px 16px', color:'#4cca74', fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.78rem', fontWeight:700, cursor:'pointer', alignSelf:'flex-end' }}>
                        Save Notes
                      </button>
                    )}
                  </div>
                </div>

                {/* Status buttons */}
                <div>
                  <div className="input-label" style={{ marginBottom:8 }}>Update Status</div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    {STATUSES.map(s => (
                      <button key={s} onClick={() => updateStatus(order.id, s)} style={{
                        padding:'6px 14px', border:'none', borderRadius:20,
                        background: order.status===s ? S_COLORS[s] : 'rgba(255,255,255,.06)',
                        color: order.status===s ? '#000' : '#888',
                        fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.75rem',
                        fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', cursor:'pointer',
                        minHeight:36,
                      }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}